import React, { useState, useMemo } from 'react';
import {
    Camera, Video, StopCircle, Activity, Info, Pencil,
    Loader2, CheckCircle2, AlertTriangle, XCircle, Circle, UploadCloud,
    Handshake, X, Clock, Lock, Siren, Archive, CalendarClock,
} from 'lucide-react';
import { FileAnalysisResultDto, Incarico } from '../../types';
import SessionTimer from '../SessionTimer';
import { IncaricoService } from '../../services/apiService';
import { useToast } from '../ToastContext';
import { UploadSummaryModal } from '../UploadSummaryModal';
import { isIncaricoConcluso, hasOrariDifformi, getOrariDifformiMinuti, useSogliaOrariDifformi, formatMinutiBreve } from '../../utils/incaricoUtils';

// ─── Upload types ──────────────────────────────────────────────────────────────
type UploadStatus = 'pending' | 'uploading' | 'ready' | 'needs_date' | 'error';
type UploadStep   = 'idle' | 'uploading' | 'confirm_dates' | 'completed';

interface UploadItem {
    file: File;
    status: UploadStatus;
    tempId?: string;
    detectedDate?: string;
    manualDate: string;
    error?: string;
}

interface Props {
    activeIncarico: Incarico;
    isStoricoActive: boolean;
    isFuturoActive: boolean;
    attivitaGps: { ore: number; ids: number[] };
    actionLoading: boolean;
    fotoDevice: string;
    setFotoDevice: (v: string) => void;
    videoDevice: string;
    setVideoDevice: (v: string) => void;
    onOpenNote: () => void;
    onStartWithPhoto: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStopWithPhoto: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRefresh: (background?: boolean) => void;
    onAccetta: () => void;
    onRifiuta: (motivo?: string) => void;
    // Apre un incarico urgente sulla stessa pratica e porta il dettaglio
    // sul nuovo incarico.
    onCreateUrgente: () => void;
}

const DEVICE_OPTIONS = ['FOTOCAMERA', 'VIDEOCAMERA', 'SMARTPHONE', 'SPY', 'ALTRO'] as const;
const DEVICE_LABELS: Record<string, string> = {
    FOTOCAMERA: 'Fotocamera', VIDEOCAMERA: 'Videocamera',
    SMARTPHONE: 'Smartphone', SPY: 'Spy', ALTRO: 'Altro',
};

const runConcurrent = async (tasks: (() => Promise<void>)[], limit: number) => {
    let i = 0;
    const worker = async () => { while (i < tasks.length) await tasks[i++](); };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
};

const StatusIcon = ({ status }: { status: UploadStatus }) => {
    if (status === 'pending')    return <Circle    size={16} className="text-slate-300" />;
    if (status === 'uploading')  return <Loader2   size={16} className="text-blue-500 animate-spin" />;
    if (status === 'ready')      return <CheckCircle2 size={16} className="text-emerald-500" />;
    if (status === 'needs_date') return <AlertTriangle size={16} className="text-amber-500" />;
    return <XCircle size={16} className="text-rose-500" />;
};

export const OpsTab: React.FC<Props> = ({
    activeIncarico, isStoricoActive, isFuturoActive, attivitaGps, actionLoading,
    fotoDevice, setFotoDevice, videoDevice, setVideoDevice,
    onOpenNote, onStartWithPhoto, onStopWithPhoto, onRefresh, onAccetta, onRifiuta,
    onCreateUrgente,
}) => {
    // In stato ASSEGNATO va accettato o rifiutato prima di poter operare.
    const isDaAccettare = activeIncarico.stato === 'ASSEGNATO' && !isStoricoActive;
    const [rifiutaModalOpen, setRifiutaModalOpen] = useState(false);
    const [motivoRifiuto, setMotivoRifiuto] = useState('');

    // Countdown alla scadenza, aggiornato ogni 30s.
    const [, forceCountdownTick] = useState(0);
    React.useEffect(() => {
        if (!isDaAccettare || !activeIncarico.dataScadenzaAccettazione) return;
        const t = setInterval(() => forceCountdownTick(v => v + 1), 30_000);
        return () => clearInterval(t);
    }, [isDaAccettare, activeIncarico.dataScadenzaAccettazione]);

    const formatTimeLeft = (until: string): { label: string; urgent: boolean; expired: boolean } => {
        const ms = new Date(until).getTime() - Date.now();
        if (ms <= 0) return { label: 'scaduta', urgent: true, expired: true };
        const min = Math.floor(ms / 60000);
        const h = Math.floor(min / 60);
        const m = min % 60;
        const d = Math.floor(h / 24);
        if (d > 0) return { label: `${d}g ${h % 24}h`, urgent: false, expired: false };
        if (h > 0) return { label: `${h}h ${m}min`, urgent: h < 2, expired: false };
        return { label: `${m} min`, urgent: true, expired: false };
    };
    const { addToast } = useToast();
    const hasActiveSession = !!(activeIncarico as any).activeSessionId;
    const activeSession = (activeIncarico as any).sessioni?.find(
        (s: any) => s.id === (activeIncarico as any).activeSessionId
    );

    // Con il report di oggi già inviato non si aprono nuove sessioni: resta
    // solo "Incarico Urgente". Non vale per lo storico.
    const isLockedByReport = !!activeIncarico.reportInviatoOggi && !isStoricoActive;

    // Revocato: stato terminale, nessuna operazione possibile, solo consultazione.
    // Il suo banner prevale su tutti gli altri.
    const isRevocato = activeIncarico.stato === 'REVOCATO';
    // Rifiutato dal collaboratore: stato terminale, solo consultazione.
    const isRifiutato = activeIncarico.stato === 'RIFIUTATO';
    // Accettato ma mai avviato: si può ancora rifiutare indicando un motivo.
    const isAccettatoNonAvviato = activeIncarico.stato === 'ACCETTATO'
        && ((activeIncarico as any).sessioni?.length ?? 0) === 0;

    // Allerta "orari difformi" ripetuta anche qui, così resta visibile per
    // tutta la durata del lavoro.
    const sogliaOrariMin = useSogliaOrariDifformi();
    const showOrariDifformi = hasOrariDifformi(activeIncarico, sogliaOrariMin);
    const minutiDifformi = showOrariDifformi ? getOrariDifformiMinuti(activeIncarico) : null;

    // Lavoro concluso (storico, COMPLETATO o tutti i report inviati): si possono
    // ancora caricare media tardivi, ma niente Start/Stop né note.
    // È distinto da isDaAccettare e da isLockedByReport.
    const isUploadOnly =
        !isDaAccettare && !isLockedByReport && !isRevocato && !isRifiutato && (
            isStoricoActive
            || activeIncarico.stato === 'COMPLETATO'
            || isIncaricoConcluso(activeIncarico)
        );

    const [uploadItems, setUploadItems]   = useState<UploadItem[]>([]);
    const [uploadStep, setUploadStep]     = useState<UploadStep>('idle');
    const [isConfirming, setIsConfirming] = useState(false);
    const [pendingUpload, setPendingUpload] = useState<{ items: UploadItem[]; category: string } | null>(null);
    const [completedSummary, setCompletedSummary] = useState<{ ok: number; discarded: number; errors: number } | null>(null);
    const [summaryResults, setSummaryResults] = useState<FileAnalysisResultDto[]>([]);
    const [showSummary, setShowSummary] = useState(false);
    // Watermark della data sì/no per il batch corrente; torna a true a ogni
    // nuovo upload, non è una preferenza globale.
    const [applyDateWatermark, setApplyDateWatermark] = useState(true);

    const updateItem = (index: number, patch: Partial<UploadItem>) =>
        setUploadItems(prev => prev.map((item, i) => i === index ? { ...item, ...patch } : item));

    const uploadProgress = useMemo(() => {
        if (uploadItems.length === 0) return 0;
        const done = uploadItems.filter(i =>
            i.status === 'ready' || i.status === 'needs_date' || i.status === 'error'
        ).length;
        return Math.round((done / uploadItems.length) * 100);
    }, [uploadItems]);

    const handleFilesSelected = (
        e: React.ChangeEvent<HTMLInputElement>,
        category: string
    ) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const items: UploadItem[] = Array.from(e.target.files).map(file => ({
            file, status: 'pending', manualDate: '',
        }));
        e.target.value = '';
        // Ogni batch riparte con il watermark attivo.
        setApplyDateWatermark(true);
        setPendingUpload({ items, category });
    };

    const confirmAndUpload = async () => {
        if (!pendingUpload) return;
        const { items, category } = pendingUpload;
        setPendingUpload(null);
        setUploadItems(items);
        setUploadStep('uploading');

        const results: UploadStatus[] = new Array(items.length).fill('pending');
        const analysisResults: FileAnalysisResultDto[] = [];

        // Il valore va catturato ora: lo state viene resettato dopo la conferma.
        const watermarkChoice = applyDateWatermark;
        const tasks = items.map((item, index) => async () => {
            setUploadItems(prev => prev.map((it, i) => i === index ? { ...it, status: 'uploading' } : it));
            try {
                const res: any = await IncaricoService.uploadMedia(
                    activeIncarico.id, item.file, category as any, activeIncarico.dataInizio,
                    undefined, undefined, watermarkChoice,
                );
                const status: UploadStatus = res?.status === 'needs_date' ? 'needs_date' : 'ready';
                results[index] = status;
                setUploadItems(prev => prev.map((it, i) =>
                    i === index ? { ...it, status, tempId: res?.tempId, detectedDate: res?.date } : it
                ));
                // Collect analysis result if backend returned extended fields
                if (res?.analysisStatus) {
                    analysisResults.push(res as FileAnalysisResultDto);
                }
            } catch (err: any) {
                results[index] = 'error';
                setUploadItems(prev => prev.map((it, i) =>
                    i === index
                        ? { ...it, status: 'error', error: err?.response?.data?.message || 'Errore' }
                        : it
                ));
            }
        });

        await runConcurrent(tasks, 4);

        // Show analysis summary if backend returned extended info
        if (analysisResults.length > 0) {
            setSummaryResults(analysisResults);
            setShowSummary(true);
            setUploadStep('idle');
            onRefresh(true);
            return;
        }

        if (results.some(r => r === 'needs_date')) {
            setUploadStep('confirm_dates');
        } else {
            const ok = results.filter(r => r === 'ready').length;
            const errors = results.filter(r => r === 'error').length;
            setCompletedSummary({ ok, discarded: 0, errors });
            setUploadStep('completed');
            onRefresh(true);
        }
    };

    const confirmDates = async () => {
        setIsConfirming(true);
        try {
            const toConfirm = uploadItems.filter(i => i.status === 'needs_date' && i.tempId && i.manualDate);
            await Promise.all(toConfirm.map(i => IncaricoService.confirmDate(i.tempId!, i.manualDate)));
            const ok = uploadItems.filter(i => i.status === 'ready').length + toConfirm.length;
            const errors = uploadItems.filter(i => i.status === 'error').length;
            setCompletedSummary({ ok, discarded: 0, errors });
            setUploadStep('completed');
            onRefresh(true);
        } catch {
            addToast('Errore nella conferma delle date', 'error');
        } finally {
            setIsConfirming(false);
        }
    };

    const skipDates = async () => {
        const orphans = uploadItems.filter(i => i.status === 'needs_date' && i.tempId);
        if (orphans.length > 0) {
            await Promise.all(
                orphans.map(i => IncaricoService.deleteMediaByTempId(i.tempId!).catch(() => null))
            );
        }
        const ok = uploadItems.filter(i => i.status === 'ready').length;
        const errors = uploadItems.filter(i => i.status === 'error').length;
        setCompletedSummary({ ok, discarded: orphans.length, errors });
        setUploadStep('completed');
        onRefresh(true);
    };

    const closeCompleted = () => {
        setCompletedSummary(null);
        resetUpload();
    };

    const resetUpload = () => {
        setUploadItems([]);
        setUploadStep('idle');
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">

            {/* Gate: incarico da accettare (o rifiutare) */}
            {isDaAccettare && (() => {
                const tl = activeIncarico.dataScadenzaAccettazione
                    ? formatTimeLeft(activeIncarico.dataScadenzaAccettazione) : null;
                return (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-[2rem] p-6 sm:p-8 shadow-xl relative overflow-hidden">
                        <div className="relative z-10 flex flex-col items-center text-center">
                            <div className="p-4 bg-white rounded-full shadow-md mb-4">
                                <Handshake size={36} className="text-amber-600"/>
                            </div>
                            <h3 className="text-xl font-black text-amber-900 mb-2">Incarico da Accettare</h3>
                            <p className="text-sm text-amber-800/80 leading-relaxed max-w-md mb-4">
                                Conferma di prendere in carico questo incarico per poter avviare sessioni operative e caricare prove.
                                In alternativa puoi rifiutarlo: l'amministratore lo riassegnerà ad un altro collaboratore.
                            </p>

                            {tl && (
                                <div className={`mb-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${
                                    tl.expired
                                        ? 'bg-rose-100 text-rose-800 border-rose-200'
                                        : tl.urgent
                                            ? 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse'
                                            : 'bg-white/70 text-amber-900 border-amber-200'
                                }`}>
                                    <Clock size={12}/>
                                    {tl.expired
                                        ? `Scadenza accettazione superata (${new Date(activeIncarico.dataScadenzaAccettazione!).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})`
                                        : `Devi rispondere entro ${tl.label}`}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                <button
                                    onClick={onAccetta}
                                    disabled={actionLoading}
                                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white py-3 px-8 rounded-xl font-bold text-base shadow-lg flex items-center justify-center gap-3 transition-colors"
                                >
                                    {actionLoading ? <Loader2 size={20} className="animate-spin"/> : <Handshake size={20}/>}
                                    {actionLoading ? 'Operazione...' : 'Accetta Incarico'}
                                </button>
                                <button
                                    onClick={() => { setMotivoRifiuto(''); setRifiutaModalOpen(true); }}
                                    disabled={actionLoading}
                                    className="bg-white hover:bg-rose-50 text-rose-700 border-2 border-rose-200 hover:border-rose-400 py-3 px-8 rounded-xl font-bold text-base shadow flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
                                >
                                    <XCircle size={20}/>
                                    Rifiuta
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Modal conferma rifiuto incarico */}
            {rifiutaModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                     onClick={() => !actionLoading && setRifiutaModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Rifiutare l'incarico?</h3>
                                <p className="text-xs text-slate-500 mt-1">L'amministratore sarà informato e potrà riassegnarlo.</p>
                            </div>
                            <button onClick={() => setRifiutaModalOpen(false)} disabled={actionLoading}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                <X size={18}/>
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                Motivo del rifiuto <span className="text-slate-400 normal-case font-normal">(opzionale)</span>
                            </label>
                            <textarea
                                value={motivoRifiuto}
                                onChange={e => setMotivoRifiuto(e.target.value)}
                                placeholder="Es. impegno concomitante, indisponibilità nella fascia oraria..."
                                rows={3}
                                maxLength={500}
                                className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none resize-none"
                            />
                        </div>
                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                            <button
                                onClick={() => setRifiutaModalOpen(false)}
                                disabled={actionLoading}
                                className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                            >Annulla</button>
                            <button
                                onClick={() => { setRifiutaModalOpen(false); onRifiuta(motivoRifiuto.trim() || undefined); }}
                                disabled={actionLoading}
                                className="px-5 py-2 text-sm font-bold bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-2"
                            >
                                {actionLoading ? <Loader2 size={14} className="animate-spin"/> : <XCircle size={14}/>}
                                Conferma Rifiuto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Allerta orari difformi, solo informativa. Nascosta se revocato o
                se la soglia non è configurata. */}
            {!isRevocato && showOrariDifformi && (
                <div
                    className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-2xl flex items-start gap-3 shadow-sm"
                    title={`Scostamento totale ${formatMinutiBreve(minutiDifformi)} oltre la soglia di ${formatMinutiBreve(sogliaOrariMin)}`}
                >
                    <AlertTriangle size={20} className="text-rose-600 shrink-0 mt-0.5"/>
                    <div className="text-sm">
                        <p className="font-black uppercase tracking-wider text-rose-700 text-xs mb-0.5">
                            Orari difformi · +{formatMinutiBreve(minutiDifformi)}
                        </p>
                        <p className="text-xs leading-snug text-rose-700/90">
                            Scostamento tra orari pianificati e reali oltre la soglia di {formatMinutiBreve(sogliaOrariMin)}.
                        </p>
                    </div>
                </div>
            )}

            {/* Revocato: sostituisce ogni altro cruscotto; i media restano
                consultabili dall'Archivio. */}
            {isRevocato && (
                <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6 flex items-start gap-4 text-red-900 shadow-sm">
                    <Lock size={26} className="text-red-600 shrink-0 mt-0.5"/>
                    <div className="text-sm">
                        <p className="font-black uppercase tracking-wider mb-1.5 text-red-700">
                            Incarico revocato dal titolare
                        </p>
                        {activeIncarico.dataRevoca && (
                            <p className="text-[11px] font-bold text-red-700/80 mb-2">
                                Revoca avvenuta il {new Date(activeIncarico.dataRevoca).toLocaleString('it-IT', {
                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit',
                                })}
                            </p>
                        )}
                        <p className="leading-relaxed">
                            Non puoi più aggiungere foto, note o report. Vai sul tab
                            <strong> Archivio</strong> per consultare i media già caricati.
                        </p>
                    </div>
                </div>
            )}

            {/* Rifiutato: solo consultazione, sostituisce ogni altro cruscotto. */}
            {isRifiutato && !isRevocato && (
                <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-6 flex items-start gap-4 text-rose-900 shadow-sm">
                    <Lock size={26} className="text-rose-600 shrink-0 mt-0.5"/>
                    <div className="text-sm">
                        <p className="font-black uppercase tracking-wider mb-1.5 text-rose-700">
                            Incarico rifiutato
                        </p>
                        {activeIncarico.motivoRifiuto && (
                            <p className="text-[11px] font-bold text-rose-700/80 mb-2">
                                Motivo: {activeIncarico.motivoRifiuto}
                            </p>
                        )}
                        <p className="leading-relaxed">
                            Hai rifiutato questo incarico, quindi è in sola consultazione.
                            Per qualunque chiarimento contatta il titolare.
                        </p>
                    </div>
                </div>
            )}

            {/* Report del giorno già inviato: niente Start/Stop, solo "Incarico Urgente". */}
            {!isDaAccettare && !isRevocato && !isRifiutato && isLockedByReport && (
                <div className="bg-gradient-to-br from-rose-50 to-amber-50 border-2 border-rose-300 rounded-[2rem] p-6 sm:p-8 shadow-xl relative overflow-hidden">
                    <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="p-4 bg-white rounded-full shadow-md mb-4">
                            <Lock size={32} className="text-rose-600"/>
                        </div>
                        <h3 className="text-xl font-black text-rose-900 mb-2">Incarico bloccato</h3>
                        <p className="text-sm text-rose-800/80 leading-relaxed max-w-md mb-5">
                            Hai già inviato il report di oggi per questo incarico, quindi le sessioni
                            sono bloccate. Se devi continuare a lavorare, apri un <strong>nuovo Incarico</strong>:
                            erediterà la pratica e partirà subito in stato attivo.
                        </p>

                        <button
                            onClick={() => {
                                if (!window.confirm("Aprire un Incarico Urgente per questa pratica?\nIl titolare riceverà una notifica.")) return;
                                onCreateUrgente();
                            }}
                            disabled={actionLoading}
                            className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white py-3.5 px-7 rounded-xl font-bold text-base shadow-lg flex items-center justify-center gap-3 transition-colors"
                        >
                            {actionLoading ? <Loader2 size={20} className="animate-spin"/> : <Siren size={20}/>}
                            {actionLoading ? 'Apertura in corso...' : 'Apri Nuovo Incarico'}
                        </button>
                    </div>
                </div>
            )}

            {/* Cruscotto GPS: solo dopo l'accettazione e finché l'incarico è operativo.
                Lock report, solo-upload e revoca lo sostituiscono. */}
            {/* Incarico che inizia in un giorno futuro: lo Start non è ancora
                disponibile (il BE risponderebbe 409). */}
            {isFuturoActive && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 flex items-start gap-4 text-indigo-900">
                    <CalendarClock size={24} className="text-indigo-600 shrink-0" />
                    <div className="text-xs font-medium">
                        <p className="font-black uppercase mb-1 tracking-wider">Incarico programmato</p>
                        <p className="leading-relaxed">
                            {`Questo incarico è programmato per il ${activeIncarico.dataInizio ? new Date(activeIncarico.dataInizio).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}. Potrai avviare la sessione a partire dal giorno di inizio.`}
                        </p>
                    </div>
                </div>
            )}

            {!isDaAccettare && !isRevocato && !isRifiutato && !isLockedByReport && !isUploadOnly && !isFuturoActive && (
                <div className="bg-slate-900 rounded-[2rem] p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-16 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
                    <div className="relative z-10 flex flex-col items-center">
                        <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-widest mb-6">
                            <Activity size={16} /> Registro attività
                        </div>

                        {hasActiveSession ? (
                            <div className="w-full flex flex-col items-center space-y-6">
                                <SessionTimer startTimestamp={activeSession?.startTime} />
                                <label className="w-full sm:w-2/3 bg-rose-600 hover:bg-rose-500 text-white py-4 rounded-xl font-semibold text-lg shadow-lg flex items-center justify-center gap-3 cursor-pointer">
                                    <StopCircle size={24} />
                                    {actionLoading ? 'Chiusura...' : 'Scatta Foto Km e Termina'}
                                    {/* Senza capture="environment" il picker offre camera e galleria;
                                        i controlli EXIF li fa il BE. */}
                                    <input type="file" accept="image/*" className="hidden" onChange={onStopWithPhoto} disabled={actionLoading} />
                                </label>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col items-center space-y-5">
                                <label className="w-full sm:w-2/3 bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-xl font-semibold text-lg shadow-lg flex items-center justify-center gap-3 cursor-pointer">
                                    <Camera size={24} />
                                    {actionLoading ? 'Avvio...' : 'Scatta Foto Km e Inizia'}
                                    <input type="file" accept="image/*" className="hidden" onChange={onStartWithPhoto} disabled={actionLoading} />
                                </label>
                                {attivitaGps.ore > 0 && (
                                    <span className="text-xs text-slate-400">Ore registrate oggi: {attivitaGps.ore}h</span>
                                )}
                                {isAccettatoNonAvviato && (
                                    <button
                                        onClick={() => { setMotivoRifiuto(''); setRifiutaModalOpen(true); }}
                                        className="text-xs font-bold text-rose-300 hover:text-rose-200 underline underline-offset-2 mt-1"
                                    >Rifiuta incarico</button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Incarico concluso: si possono ancora caricare media tardivi,
                ma non aprire sessioni né scrivere note. */}
            {!isDaAccettare && !isLockedByReport && isUploadOnly && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-4 text-emerald-900">
                    {isStoricoActive
                        ? <Archive size={24} className="text-emerald-600 shrink-0" />
                        : <CheckCircle2 size={24} className="text-emerald-600 shrink-0" />}
                    <div className="text-xs font-medium">
                        <p className="font-black uppercase mb-1 tracking-wider">
                            {isStoricoActive ? 'Modalità Archivio · solo upload' : 'Incarico Conferito · solo upload tardivo'}
                        </p>
                        <p className="leading-relaxed">
                            {isStoricoActive
                                ? 'Le sessioni GPS e le note testuali sono disabilitate. Puoi caricare ulteriore materiale multimediale (Foto/Video) se necessario.'
                                : 'Hai concluso il ciclo operativo di questo incarico. Le sessioni GPS e le note sono chiuse: puoi però caricare qui sotto eventuali foto/video dimenticati in campo.'}
                        </p>
                    </div>
                </div>
            )}

            {/* Upload prove: nascosto se revocato, rifiutato o ancora da accettare. */}
            {!isRevocato && !isRifiutato && !isDaAccettare && !isFuturoActive && (
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4 px-1">
                    Integrazione Prove Multiplo
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">

                    {/* Foto */}
                    <div className="flex flex-col p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <Camera size={18} className="text-blue-500" />
                            <select
                                className="text-[10px] font-bold uppercase text-slate-600 bg-slate-100 rounded p-1 max-w-[110px]"
                                value={fotoDevice}
                                onChange={e => setFotoDevice(e.target.value)}
                            >
                                {DEVICE_OPTIONS.map(d => <option key={d} value={d}>{DEVICE_LABELS[d]}</option>)}
                            </select>
                        </div>
                        <label className="flex flex-col items-center py-6 rounded-xl cursor-pointer hover:bg-slate-50 border-2 border-dashed border-slate-200 transition-colors">
                            <UploadCloud size={20} className="text-blue-400 mb-1" />
                            <span className="font-bold text-xs">SELEZIONA FOTO</span>
                            <input
                                type="file" multiple accept="image/*" className="hidden"
                                onChange={e => handleFilesSelected(e, fotoDevice)}
                            />
                        </label>
                    </div>

                    {/* Video */}
                    <div className="flex flex-col p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <Video size={18} className="text-rose-500" />
                            <select
                                className="text-[10px] font-bold uppercase text-slate-600 bg-slate-100 rounded p-1 max-w-[110px]"
                                value={videoDevice}
                                onChange={e => setVideoDevice(e.target.value)}
                            >
                                {DEVICE_OPTIONS.map(d => <option key={d} value={d}>{DEVICE_LABELS[d]}</option>)}
                            </select>
                        </div>
                        <label className="flex flex-col items-center py-6 rounded-xl cursor-pointer hover:bg-slate-50 border-2 border-dashed border-slate-200 transition-colors">
                            <UploadCloud size={20} className="text-rose-400 mb-1" />
                            <span className="font-bold text-xs">SELEZIONA VIDEO</span>
                            <input
                                type="file" multiple accept="video/*" className="hidden"
                                onChange={e => handleFilesSelected(e, videoDevice)}
                            />
                        </label>
                    </div>

                    {/* Nota testuale: disabilitata quando l'incarico non è operativo */}
                    <button
                        onClick={onOpenNote}
                        disabled={isStoricoActive || isUploadOnly || isLockedByReport}
                        className="col-span-2 flex items-center justify-center gap-3 p-4 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold disabled:opacity-40 transition-colors hover:bg-slate-50"
                    >
                        <Pencil size={20} className="text-amber-500" />
                        Registra Osservazione
                    </button>
                </div>
            </div>
            )}

            {/* ── OVERLAY CONFERMA UPLOAD ────────────────────────────────────── */}
            {pendingUpload && (
                <div className="fixed inset-0 z-[200] bg-slate-900/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
                    <div className="bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-5">
                        <div className="flex flex-col items-center text-center gap-3">
                            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                                <UploadCloud size={26} className="text-amber-500" />
                            </div>
                            <div>
                                <p className="font-black text-slate-900 text-base uppercase tracking-wide">Conferma caricamento</p>
                                <p className="text-sm text-slate-500 mt-1.5">
                                    Stai per caricare{' '}
                                    <span className="font-black text-slate-800">{pendingUpload.items.length} {pendingUpload.items.length === 1 ? 'file' : 'file'}</span>
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Incarico {activeIncarico.soggettoNome} · {pendingUpload.category}
                                </p>
                            </div>
                        </div>

                        {/* Watermark della data su tutte le immagini del batch (default sì) */}
                        <label className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={applyDateWatermark}
                                onChange={e => setApplyDateWatermark(e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-black text-slate-700 uppercase tracking-tight">Applica data come watermark</p>
                                <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                                    Stampa la data di acquisizione (EXIF) sull'immagine. Disattiva se la foto la contiene già.
                                </p>
                            </div>
                        </label>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setPendingUpload(null)}
                                className="flex-1 py-3.5 text-slate-500 font-black text-[11px] uppercase border border-slate-200 rounded-2xl hover:bg-slate-50 transition-colors"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmAndUpload}
                                className="flex-[2] py-3.5 bg-blue-600 text-white rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition-all"
                            >
                                <UploadCloud size={15} /> Conferma
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── UPLOAD SUMMARY MODAL ───────────────────────────────────────── */}
            <UploadSummaryModal
                isOpen={showSummary}
                onClose={() => { setShowSummary(false); onRefresh(true); }}
                results={summaryResults}
                incaricoId={activeIncarico.id}
                context="collaboratore"
                onPollStatus={IncaricoService.getMediaAnalysisStatus}
                onManualDateSave={async (mediaId, tempId, date, time) => {
                    const iso = `${date}T${time}:00`;
                    await IncaricoService.confirmDate(tempId, iso);
                }}
                onDiscard={async (_mediaId, tempId) => {
                    await IncaricoService.deleteMediaByTempId(tempId);
                    addToast('File scartato', 'info');
                }}
                onSubmitForApproval={async (items) => {
                    await IncaricoService.submitPendingApproval(activeIncarico.id, items);
                    addToast('File inviati in approvazione al responsabile/titolare', 'success');
                }}
            />

            {/* ── OVERLAY UPLOAD ─────────────────────────────────────────────── */}
            {uploadStep !== 'idle' && (
                <div className="fixed inset-0 z-[200] bg-slate-900/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
                    <div className="bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-5">

                        {/* Step: caricamento */}
                        {uploadStep === 'uploading' && (
                            <>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
                                        <UploadCloud className="text-blue-600" size={22} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-black text-slate-900 uppercase text-sm">Caricamento in corso…</h3>
                                        <p className="text-[11px] text-slate-400 font-bold">{uploadItems.length} file</p>
                                    </div>
                                    <span className="text-2xl font-black text-blue-600">{uploadProgress}%</span>
                                </div>

                                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>

                                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                                    {uploadItems.map((item, i) => (
                                        <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                                            <StatusIcon status={item.status} />
                                            <span className="text-xs font-medium text-slate-700 truncate flex-1">{item.file.name}</span>
                                            {item.status === 'ready' && item.detectedDate && (
                                                <span className="text-[10px] text-emerald-600 font-bold shrink-0">
                                                    {new Date(item.detectedDate).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            )}
                                            {item.status === 'error' && (
                                                <span className="text-[10px] text-rose-500 font-bold shrink-0">Errore</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Step: conferma date mancanti */}
                        {uploadStep === 'confirm_dates' && (
                            <>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
                                    <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                    <p className="text-sm text-amber-800 font-medium">
                                        {uploadItems.filter(i => i.status === 'needs_date').length} file senza data EXIF.
                                        Inserisci la data di acquisizione reale.
                                    </p>
                                </div>

                                <div className="space-y-3 max-h-60 overflow-y-auto">
                                    {uploadItems.map((item, i) => item.status !== 'needs_date' ? null : (
                                        <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                                            <p className="text-xs font-bold text-slate-700 mb-2 truncate">{item.file.name}</p>
                                            <input
                                                type="datetime-local"
                                                className="w-full text-sm p-2 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                                value={item.manualDate}
                                                onChange={e => updateItem(i, { manualDate: e.target.value })}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {uploadItems.some(i => i.status === 'ready') && (
                                    <p className="text-xs text-slate-400 text-center">
                                        + {uploadItems.filter(i => i.status === 'ready').length} file già confermati con data EXIF
                                    </p>
                                )}

                                <div className="flex gap-3">
                                    <button
                                        onClick={skipDates}
                                        className="flex-1 py-3.5 text-slate-500 font-black text-[11px] uppercase border border-slate-200 rounded-2xl hover:bg-slate-50 transition-colors"
                                    >
                                        Salta
                                    </button>
                                    <button
                                        onClick={confirmDates}
                                        disabled={isConfirming || uploadItems.filter(i => i.status === 'needs_date').some(i => !i.manualDate)}
                                        className="flex-[2] py-3.5 bg-blue-600 text-white rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg active:scale-[0.98] transition-all"
                                    >
                                        {isConfirming
                                            ? <><Loader2 className="animate-spin" size={15} /> Conferma…</>
                                            : <><CheckCircle2 size={15} /> Conferma Date</>
                                        }
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Step: completato */}
                        {uploadStep === 'completed' && completedSummary && (
                            <>
                                <div className="flex flex-col items-center text-center gap-3">
                                    <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                                        <CheckCircle2 size={28} className="text-emerald-500" />
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-900 text-base uppercase tracking-wide">Caricamento completato</p>
                                        <p className="text-sm text-slate-500 mt-1.5">
                                            <span className="font-black text-emerald-600">{completedSummary.ok}</span> file caricati con successo
                                        </p>
                                        {completedSummary.discarded > 0 && (
                                            <p className="text-xs text-amber-600 mt-1 font-bold">
                                                {completedSummary.discarded} scartati (data mancante)
                                            </p>
                                        )}
                                        {completedSummary.errors > 0 && (
                                            <p className="text-xs text-rose-600 mt-1 font-bold">
                                                {completedSummary.errors} errori
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={closeCompleted}
                                    className="w-full py-3.5 bg-emerald-600 text-white rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition-all"
                                >
                                    OK
                                </button>
                            </>
                        )}

                    </div>
                </div>
            )}
        </div>
    );
};
